import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Layout,
  Menu,
  Button,
  Card,
  Spin,
  message,
  Typography,
  Tag,
  Radio,
  Space,
  Alert,
  Collapse,
  Divider,
} from "antd";
import {
  RocketOutlined,
  CodeOutlined,
  QuestionCircleOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { courseModules } from "../data/courseContent";
import { MarkdownRenderer } from "./MarkdownRenderer";
import axios from "axios";

const { Header, Content } = Layout;
const { Title } = Typography;

export const CourseLayout: React.FC = () => {
  // Grab the dynamic ID from the URL and initialize navigation
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State management for API interaction
  const [apiResponse, setApiResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // State tracking for interactive quizzes
  const [quizAnswers, setQuizAnswers] = useState<Record<number, number>>({});
  const [quizSubmitted, setQuizSubmitted] = useState<Record<number, boolean>>(
    {},
  );

  // Reset interactive states whenever the lesson ID changes in the URL
  useEffect(() => {
    setApiResponse(null);
    setQuizAnswers({});
    setQuizSubmitted({});
  }, [id]);

  // Derive the current lesson directly from the URL parameter
  let currentLesson = courseModules[0].lessons[0];
  for (const mod of courseModules) {
    const lesson = mod.lessons.find((l) => l.id === id);
    if (lesson) {
      currentLesson = lesson;
      break;
    }
  }

  // Handle navigation click by updating the URL
  const handleMenuClick = (lessonId: string) => {
    navigate(`/lesson/${lessonId}`);
  };

  // Live test request mimicking Postman functionality via Axios
  const triggerSandboxRequest = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        "https://jsonplaceholder.typicode.com/todos/1",
      );
      setApiResponse(JSON.stringify(res.data, null, 2));
      message.success("API Request Successful!");
    } catch (error) {
      setApiResponse("Error fetching data");
      message.error("API Request Failed");
    } finally {
      setLoading(false);
    }
  };

  // Dynamically map modules to Ant Design Menu items
  const menuItems = courseModules.map((mod) => ({
    key: mod.id,
    label: mod.title,
    children: mod.lessons.map((lesson) => ({
      key: lesson.id,
      label: lesson.title,
    })),
  }));

  return (
    <Layout style={{ minHeight: "100vh", background: "#F8FAFC" }}>
      <Header
        style={{
          display: "flex",
          alignItems: "center",
          background: "#ffffff",
          padding: "0 24px",
          borderBottom: "1px solid #E2E8F0",
          boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
          zIndex: 10,
          width: "100%",
        }}
      >
        <Link
          to="/"
          style={{ display: "flex", alignItems: "center", marginRight: 40 }}
        >
          <Title
            level={3}
            style={{
              color: "#0F172A",
              margin: 0,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              marginRight: "40px",
              whiteSpace: "nowrap",
            }}
          >
            <RocketOutlined
              style={{ color: "#FF6C37", marginRight: 12, fontSize: "28px" }}
            />
            Learn Postman
          </Title>
        </Link>

        <Menu
          mode="horizontal"
          selectedKeys={[currentLesson.id]}
          style={{
            flex: 1,
            minWidth: 0,
            borderBottom: "none",
            lineHeight: "64px",
          }}
          items={menuItems}
          onClick={({ key }) => handleMenuClick(key)}
        />
      </Header>

      <Layout style={{ background: "transparent" }}>
        <Layout style={{ padding: "32px 40px" }}>
          <Content
            style={{
              background: "#fff",
              padding: "40px",
              margin: "0 auto",
              minHeight: 280,
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
              maxWidth: "900px",
              width: "100%",
            }}
          >
            <MarkdownRenderer content={currentLesson.content} />

            {/* Render Exercise if available */}
            {currentLesson.exercise && (
              <div style={{ marginTop: 40 }}>
                <Divider />
                <Title
                  level={4}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <CodeOutlined style={{ marginRight: 8, color: "#FF6C37" }} />
                  Exercise: {currentLesson.exercise.title}
                </Title>
                <Card
                  style={{
                    borderRadius: 12,
                    borderColor: "#E2E8F0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                  }}
                >
                  <p style={{ fontSize: "1rem", color: "#475569" }}>
                    {currentLesson.exercise.description}
                  </p>

                  {currentLesson.exercise.starterCode && (
                    <div style={{ marginTop: 20 }}>
                      <h5 style={{ margin: "0 0 8px 0", color: "#1E293B" }}>
                        Starter Code:
                      </h5>
                      <pre
                        style={{
                          background: "#F8FAFC",
                          padding: 16,
                          borderRadius: 8,
                          border: "1px solid #E2E8F0",
                          overflowX: "auto",
                          color: "#334155",
                        }}
                      >
                        {currentLesson.exercise.starterCode}
                      </pre>
                    </div>
                  )}

                  {currentLesson.exercise.solution && (
                    <Collapse ghost style={{ marginTop: 16, padding: 0 }}>
                      <Collapse.Panel
                        header={
                          <span style={{ fontWeight: 600, color: "#FF6C37" }}>
                            <BulbOutlined style={{ marginRight: 8 }} />
                            View Solution
                          </span>
                        }
                        key="1"
                        style={{ padding: 0 }}
                      >
                        <pre
                          style={{
                            background: "#F0FDF4",
                            padding: 16,
                            borderRadius: 8,
                            border: "1px solid #BBF7D0",
                            overflowX: "auto",
                            color: "#166534",
                          }}
                        >
                          {currentLesson.exercise.solution}
                        </pre>
                      </Collapse.Panel>
                    </Collapse>
                  )}
                </Card>
              </div>
            )}

            {/* Render Quiz if available */}
            {currentLesson.quiz && currentLesson.quiz.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <Divider />
                <Title
                  level={4}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <QuestionCircleOutlined
                    style={{ marginRight: 8, color: "#3B82F6" }}
                  />
                  Knowledge Check
                </Title>
                {currentLesson.quiz.map((q, index) => (
                  <Card
                    key={index}
                    style={{
                      marginBottom: 16,
                      borderRadius: 12,
                      borderColor: "#E2E8F0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "1.05rem",
                        color: "#1E293B",
                        fontWeight: 600,
                      }}
                    >
                      {q.question}
                    </p>
                    <Radio.Group
                      onChange={(e) =>
                        setQuizAnswers({
                          ...quizAnswers,
                          [index]: e.target.value,
                        })
                      }
                      value={quizAnswers[index]}
                      disabled={quizSubmitted[index]}
                      style={{ display: "block", marginTop: 16 }}
                    >
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {q.options.map((opt, optIndex) => (
                          <Radio
                            key={optIndex}
                            value={optIndex}
                            style={{ fontSize: "1rem", color: "#475569" }}
                          >
                            {opt}
                          </Radio>
                        ))}
                      </Space>
                    </Radio.Group>

                    <div style={{ marginTop: 24 }}>
                      <Button
                        type="primary"
                        onClick={() =>
                          setQuizSubmitted({ ...quizSubmitted, [index]: true })
                        }
                        disabled={
                          quizAnswers[index] === undefined ||
                          quizSubmitted[index]
                        }
                        style={{
                          background: quizSubmitted[index]
                            ? "#94A3B8"
                            : "#FF6C37",
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      >
                        {quizSubmitted[index] ? "Submitted" : "Submit Answer"}
                      </Button>
                    </div>

                    {quizSubmitted[index] && (
                      <Alert
                        style={{ marginTop: 20, borderRadius: 8 }}
                        type={
                          quizAnswers[index] === q.correctAnswer
                            ? "success"
                            : "error"
                        }
                        message={
                          quizAnswers[index] === q.correctAnswer
                            ? "Correct!"
                            : "Incorrect"
                        }
                        description={q.explanation}
                        showIcon
                      />
                    )}
                  </Card>
                ))}
              </div>
            )}

            {/* Interactive Sandbox */}
            {currentLesson.id === "get-request" && (
              <Card
                title={
                  <>
                    <CodeOutlined
                      style={{ marginRight: 8, color: "#FF6C37" }}
                    />
                    Interactive Sandbox
                  </>
                }
                style={{
                  marginTop: 40,
                  borderColor: "#E2E8F0",
                  borderRadius: 12,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                }}
                headStyle={{
                  borderBottom: "1px solid #E2E8F0",
                  backgroundColor: "#F8FAFC",
                  borderRadius: "12px 12px 0 0",
                }}
              >
                <p
                  style={{
                    fontSize: "1rem",
                    color: "#475569",
                    marginBottom: 20,
                  }}
                >
                  Click below to simulate a Postman <Tag color="blue">GET</Tag>{" "}
                  request to{" "}
                  <code
                    style={{
                      background: "#F1F5F9",
                      padding: "4px 8px",
                      borderRadius: 4,
                    }}
                  >
                    https://jsonplaceholder.typicode.com/todos/1
                  </code>
                </p>

                <Button
                  type="primary"
                  size="large"
                  icon={<RocketOutlined />}
                  onClick={triggerSandboxRequest}
                  loading={loading}
                  style={{ fontWeight: 600 }}
                >
                  Send GET Request
                </Button>

                {loading && (
                  <div style={{ marginTop: 24, textAlign: "center" }}>
                    <Spin tip="Fetching response..." size="large" />
                  </div>
                )}

                {apiResponse && (
                  <div style={{ marginTop: 30 }}>
                    <h4
                      style={{
                        margin: "0 0 12px 0",
                        color: "#1E293B",
                        fontWeight: 700,
                      }}
                    >
                      Response Body:
                    </h4>
                    <pre
                      style={{
                        background: "#0F172A",
                        color: "#38BDF8",
                        padding: 20,
                        borderRadius: 8,
                        overflowX: "auto",
                        fontSize: "0.95rem",
                        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    >
                      {apiResponse}
                    </pre>
                  </div>
                )}
              </Card>
            )}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};
